import { FlexBox, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { useCallback } from 'react';

import { aiApi } from 'apis/ai';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type CopilotDropdownHeaderFeedbackProps = {
  /**
   * When a feedbackId is given as either a string or null, the feedback buttons
   * will be rendered. If left undefined feedback buttons will not be rendered.
   */
  feedbackId?: string | null;
  /** Additional arbitrary feedback */
  additionalFeedback?: Record<string, string | null>;
};

export const CopilotDropdownHeaderFeedback = ({
  feedbackId,
  additionalFeedback = {},
}: CopilotDropdownHeaderFeedbackProps): JSX.Element | null => {
  const dispatch = useAppDispatch();

  const onFeedback = useCallback(
    async (isUseful: boolean) => {
      const { isSent, feedbackMessage } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:feedbackForm',
            arg: {
              title: 'AI Copilot Feedback',
              team: 'conversations',
              placeholder: isUseful
                ? 'What did you like about the feature?'
                : 'What was the issue with the feature? How could it be improved?',
              extra: {
                ...additionalFeedback,
                feedbackId: feedbackId || '',
              },
            },
          },
        }),
      );

      if (isSent && feedbackMessage && feedbackId) {
        dispatch(
          aiApi.endpoints.createAiFeedback.initiate({
            feedbackId: feedbackId,
            feedback: [
              {
                type: 'rating',
                value: isUseful ? 'good' : 'bad',
              },
              {
                type: 'comment',
                value: feedbackMessage,
              },
            ],
          }),
        );
      }
    },
    [additionalFeedback, dispatch, feedbackId],
  );

  if (feedbackId === undefined) {
    return null;
  }

  return (
    <FlexBox flex="0 1 100px" justifyContent="flex-end" gap="8px">
      <Tooltip content="This is not helpful" dark>
        <IconButton
          variant="tertiary"
          icon="thumbs-down"
          onClick={() => onFeedback(false)}
        />
      </Tooltip>
      <Tooltip content="This is helpful" dark>
        <IconButton
          variant="tertiary"
          icon="thumbs-up"
          onClick={() => onFeedback(true)}
        />
      </Tooltip>
    </FlexBox>
  );
};
